<template>
  <div>
    <loading-container v-if="loading"></loading-container>

    <div v-else class="container">
      <div class="row">
        <div class="col-xs-6">
          <help-text>
            <h3>
              Edit Subscription
            </h3>

            <p>
              Edit the details of the subscription.
            </p>

            <div class="btn-group">
              <button type="button" class="btn btn-black btn-sm dropdown-toggle" data-toggle="dropdown">
                <i class="fa fa-cogs"></i> Actions <span class="caret"></span>
              </button>

              <ul class="dropdown-menu">
                <li :class="{'disabled': celebrationDisabled}">
                  <a href="#" @click.prevent="celebrate">
                    <i class="fa fa-glass-cheers"></i> Celebrate
                  </a>
                </li>
              </ul>
            </div>
          </help-text>
        </div>

        <div class="col-xs-18">
          <div class="panel panel-default">
            <div class="panel-body">
              <form @submit.prevent="submit" class="form-horizontal">
                <static-form-group
                    :value="subscription.data.legacy_id"
                    label="Legacy ID"
                    help-text="The ID of the subscription in the legacy system."
                ></static-form-group>

                <select-form-group
                    v-model="subscription.data.subscription_type_id"
                    name="subscription_type_id"
                    label="Subscription Type"
                    help-text="Any subscription type to use for the subscription."
                    :error="getError('subscription_type_id')"
                    :options="subscriptionTypeOptions"
                    :nullable="true"
                    placeholder="(none)"
                ></select-form-group>

                <number-form-group
                    v-model="subscription.data.price"
                    name="price"
                    label="Price"
                    help-text="The future price of subscription periods in this subscription."
                    :error="getError('price')"
                    :disabled="missingPermission('admin')"
                ></number-form-group>

                <number-form-group
                    v-model="subscription.data.period"
                    name="period"
                    label="Period"
                    help-text="The future length (in months) of subscription periods in this subscription."
                    :error="getError('period')"
                    :disabled="missingPermission('admin')"
                ></number-form-group>

                <number-form-group
                    v-model="subscription.data.closes_after"
                    name="closes_after"
                    label="Closes After"
                    help-text="The future number of days that the subscription will remain active when not paid."
                    :error="getError('closes_after')"
                ></number-form-group>

                <number-form-group
                    v-model="subscription.data.seats"
                    name="seats"
                    label="Seats"
                    help-text="The number of seats that the subscription will grant on the portal."
                    :error="getError('seats')"
                ></number-form-group>

                <text-form-group
                    v-model="subscription.data.purchase_order"
                    name="purchase_order"
                    label="Purchase Order (PO number)"
                    help-text="The subscriber's purchase order (not required)."
                    :error="getError('purchase_order')"
                ></text-form-group>

                <radio-form-group
                    v-model="subscription.data.has_automatic_renewal"
                    name="has_automatic_renewal"
                    label="Automatic Renewal"
                    help-text="Select this option if the subscription should automatically be renewed."
                    :error="getError('has_automatic_renewal')"
                    :options="[{value: true, label: 'Yes'}, {value: false, label: 'No'}]"
                ></radio-form-group>

                <radio-form-group
                    v-model="subscription.data.is_free"
                    name="is_free"
                    label="Free"
                    help-text="Select this option if the subscription should be free."
                    :error="getError('is_free')"
                    :options="[{value: true, label: 'Yes'}, {value: false, label: 'No'}]"
                ></radio-form-group>

                <select-form-group
                    v-model="subscription.data.user_id"
                    name="user_id"
                    label="User"
                    help-text="The user that is responsible for the subscription."
                    :error="getError('user_id')"
                    :options="userOptions"
                    :nullable="true"
                ></select-form-group>

                <select-form-group
                    v-model="subscription.data.opportunity_id"
                    name="opportunity_id"
                    label="Opportunity"
                    help-text="The opportunity that is converted into this subscription."
                    :error="getError('opportunity_id')"
                    :options="opportunityOptions"
                    :nullable="true"
                ></select-form-group>

                <select-form-group
                    v-model="subscription.data.meeting_id"
                    name="meeting_id"
                    label="Meeting"
                    help-text="The meeting that is converted into this subscription."
                    :error="getError('meeting_id')"
                    :options="meetingOptions"
                    :nullable="true"
                    :disabled="subscription.data.opportunity_id !== null && subscription.data.opportunity_id !== ''"
                ></select-form-group>

                <select-form-group
                    v-model="subscription.data.contact_person_id"
                    name="contact_person_id"
                    label="Contact Person"
                    help-text="The contact person in the company."
                    :error="getError('contact_person_id')"
                    :options="personOptions"
                    :nullable="true"
                ></select-form-group>

                <typeahead-form-group
                    v-model="subscription.data.other_payer_id"
                    name="other_payer_id"
                    label="Other Payer"
                    help-text="The company that pays for the subscription (optional)."
                    :error="getError('other_payer_id')"
                    endpoint="/api/v2/companies"
                    :token="shared.token"
                    path="data.data"
                    placeholder="Search..."
                    :initial-label="subscription.data.other_payer ? subscription.data.other_payer.name : ''"
                    :result-label-property="row => row.attributes.name"
                ></typeahead-form-group>

                <typeahead-form-group
                    v-model="subscription.data.other_payer_contact_person_id"
                    name="other_payer_contact_person_id"
                    label="Other Payer Contact Person"
                    help-text="The contact person in the company that pays for the subscription (if not the company itself)."
                    :error="getError('other_payer_contact_person_id')"
                    endpoint="/api/v2/people"
                    :params="{'filter[company_id]': subscription.data.other_payer_id}"
                    :token="shared.token"
                    path="data.data"
                    placeholder="Search..."
                    :initial-label="subscription.data.other_payer_contact_person ? subscription.data.other_payer_contact_person.name : ''"
                    :result-label-property="row => row.attributes.name"
                ></typeahead-form-group>

                <static-form-group
                    :value="subscription.data.created_at | datetime"
                    label="Created At"
                    help-text="The time that the subscription was created."
                ></static-form-group>

                <hr>

                <submit-form-group button-class="btn-black"></submit-form-group>
              </form>
            </div>
          </div>
        </div>
      </div>

      <hr class="panel-separator">

      <div class="row">
        <div class="col-xs-6">
          <help-text>
            <h3>
              Activity
            </h3>

            <p>
              This table displays the activity of the subscription.
            </p>
          </help-text>
        </div>

        <div class="col-xs-18">
          <Partnership
              :partnership="partnership.data"
              :show-header="false"
          />
        </div>
      </div>

      <hr class="panel-separator">

      <div class="row">
        <div class="col-xs-6">
          <help-text>
            <h3>
              Subscription Periods
            </h3>

            <p>
              These are the periods where this subscription is active.
            </p>

            <a href="#create-subscription-period-modal" class="btn btn-black btn-sm" data-toggle="modal">
              <i class="fa fa-plus"></i> New
            </a>
          </help-text>
        </div>

        <div class="col-xs-18">
          <subscription-period-list :subscription="subscription"></subscription-period-list>
        </div>
      </div>

      <hr class="panel-separator">

      <div class="row">
        <div class="col-xs-6">
          <help-text>
            <h3>
              Magazine Recipients
            </h3>

            <p>
              This table lists the people that will receive the magazine from this subscription.
            </p>

            <a href="#create-magazine-recipient-modal" class="btn btn-black btn-sm" data-toggle="modal">
              <i class="fa fa-plus"></i> New
            </a>
          </help-text>
        </div>

        <div class="col-xs-18">
          <magazine-recipient-list :subscription="subscription"></magazine-recipient-list>
        </div>
      </div>

      <celebrate-subscription-modal
          :subscription="subscription"
          @celebrated="celebrated"
      ></celebrate-subscription-modal>
    </div>
  </div>
</template>

<script>
import http from '@/http'
import modal from '@/modal'
import moment from 'moment'
import notify from '@/notify'
import store from '@/store'
import CelebrateSubscriptionModal from '@/components/Modals/Subscriptions/CelebrateSubscriptionModal'
import Errors from '@/mixins/Errors'
import HelpText from '@/components/HelpText'
import Load from '@/mixins/Load'
import LoadingContainer from '@/components/Layout/LoadingContainer'
import MagazineRecipientList from '@/pages/Shared/Panels/MagazineRecipientList'
import NumberFormGroup from '@/vendor/forms/FormGroups/Horizontal/Number'
import Permissions from '@/mixins/Permissions'
import RadioFormGroup from '@/vendor/forms/FormGroups/Horizontal/Radio'
import SelectFormGroup from '@/vendor/forms/FormGroups/Horizontal/Select'
import StaticFormGroup from '@/vendor/forms/FormGroups/Horizontal/Static'
import StatusLabel from '@/components/StatusLabel'
import Partnership from '@/pages/Companies/Companies/Edit/Tabs/Dashboard/Panels/Partnership'
import SubscriptionPeriodList from '@/pages/Shared/Panels/SubscriptionPeriodList'
import SubmitFormGroup from '@/vendor/forms/FormGroups/Horizontal/Submit'
import TextFormGroup from '@/vendor/forms/FormGroups/Horizontal/Text'
import TypeaheadFormGroup from '@/vendor/forms/FormGroups/Horizontal/Typeahead'

export default {
  components: {
    CelebrateSubscriptionModal,
    HelpText,
    LoadingContainer,
    MagazineRecipientList,
    NumberFormGroup,
    RadioFormGroup,
    SelectFormGroup,
    StaticFormGroup,
    StatusLabel,
    Partnership,
    SubmitFormGroup,
    SubscriptionPeriodList,
    TextFormGroup,
    TypeaheadFormGroup
  },

  mixins: [Errors, Load, Permissions],

  model: {
    prop: 'company',
    event: 'company-updated'
  },

  props: ['company', 'subscriptionId'],

  data() {
    return {
      subscription: null,
      partnership: null,
      users: null,
      people: null,
      meetings: null,
      opportunities: null,
      shared: store
    }
  },

  mounted() {
    this.fetch()
  },

  watch: {
    'subscription.data.opportunity_id'(value) {
      if (!value) {
        return this.subscription.meeting_id = null
      }

      this.subscription.data.meeting_id = this.opportunities.data.find(opportunity => opportunity.id === parseInt(value)).attributes.meeting_id
    }
  },

  computed: {
    userOptions() {
      return this.users.data.map(user => {
        return {
          value: user.id,
          label: user.attributes.full_name
        }
      })
    },

    personOptions() {
      return this.people.data.map(person => {
        return {
          value: person.id,
          label: person.name
        }
      })
    },

    opportunityOptions() {
      return this.opportunities.data.map(opportunity => {
        return {
          value: opportunity.id,
          label: opportunity.attributes.user_full_name + ' - ' + this.$options.filters.date(opportunity.attributes.conversion_expected_at) + ' - ' + this.$options.filters.price(opportunity.attributes.value)
        }
      })
    },

    meetingOptions() {
      return this.meetings.data.map(meeting => {
        return {
          value: meeting.id,
          label: (meeting.attributes.title || 'Untitled meeting') + ' (' + this.$options.filters.datetime(meeting.attributes.starts_at) + ')'
        }
      })
    },

    subscriptionTypeOptions() {
      return this.subscriptionTypes.data
          .filter(subscriptionType => subscriptionType.attributes.portal_id === this.subscription.data.portal_id)
          .map(subscriptionType => {
            return {
              value: subscriptionType.id,
              label: subscriptionType.attributes.name
            }
          })
    },

    celebrationDisabled() {
      return this.subscription.data.celebrated_at || !this.subscription.data.subscription_periods.length
    }
  },

  methods: {
    fetch() {
      this.load({
        subscription: '/api/v1/subscriptions/' + this.subscriptionId,
        partnership: '/api/v2/partnerships/' + this.subscriptionId,
        users: '/api/v2/users',
        opportunities: '/api/v2/opportunities?filter[company_id]=' + this.company.data.id,
        meetings: '/api/v2/meetings?filter[company_id]=' + this.company.data.id,
        people: '/api/v1/companies/' + this.company.data.id + '/people',
        subscriptionTypes: '/api/v2/subscription-types'
      })
    },

    submit() {
      http.put('/api/v1/subscriptions/' + this.subscription.data.id, this.subscription.data)
          .then(() => {
            notify.success('The subscription was saved')

            this.errors = {}
          })
          .catch(error => {
            if (error.response.status === 400) {
              return notify.error(error.response.data.message)
            }

            throw error
          })
          .catch(this.catchValidationErrors)
    },

    destroy(subscriptionPeriod) {
      modal.confirm('Are you sure that you want to delete the subscription period?', () => {
        this.getDestroyPromise(subscriptionPeriod)
            .then(() => {
              notify.success('The subscription period was deleted')

              this.subscription.data.subscription_periods = this.subscription.data.subscription_periods.filter(existing => {
                return existing.id === subscriptionPeriod.id
              })
            })
      })
    },

    getDestroyPromise(subscriptionPeriod) {
      return http.delete('/api/v1/subscription-periods/' + subscriptionPeriod.id)
    },

    celebrate() {
      if (this.celebrationDisabled) {
        return
      }

      $('#celebrate-subscription-modal').modal('show')
    },

    celebrated() {
      notify.success('The partnership was celebrated')

      this.subscription.data.celebrated_at = moment().format('YYYY-MM-DD')
    }
  }
}
</script>
